import React, { useRef } from 'react';
import { FiUpload, FiSearch, FiSend, FiPhone, FiStar, FiZap, FiEdit3, FiBookmark, FiXCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const ICON_MAP = {
  upload: { icon: FiUpload, bg: 'bg-blue-100', color: 'text-blue-600' },
  diagnosis: { icon: FiSearch, bg: 'bg-purple-100', color: 'text-purple-600' },
  polish: { icon: FiEdit3, bg: 'bg-violet-100', color: 'text-violet-600' },
  save: { icon: FiBookmark, bg: 'bg-indigo-100', color: 'text-indigo-600' },
  apply: { icon: FiSend, bg: 'bg-blue-100', color: 'text-blue-600' },
  tailor: { icon: FiEdit3, bg: 'bg-primary-100', color: 'text-primary-600' },
  interview: { icon: FiPhone, bg: 'bg-amber-100', color: 'text-amber-600' },
  streak: { icon: FiZap, bg: 'bg-yellow-100', color: 'text-yellow-600' },
  offer: { icon: FiStar, bg: 'bg-green-100', color: 'text-green-600' },
  reject: { icon: FiXCircle, bg: 'bg-red-100', color: 'text-red-600' },
};

const OUTCOME_STYLES = {
  offered: { border: 'border-green-400', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
  interviewing: { border: 'border-blue-400', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
  rejected: { border: 'border-gray-300', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-600' },
  applied: { border: 'border-amber-400', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' },
};

const TimelineEvent = ({ event }) => {
  const config = ICON_MAP[event.icon] || ICON_MAP.apply;
  const Icon = config.icon;
  const isHighlight = event.type === 'offer' || event.type === 'streak';

  return (
    <div className={`flex-shrink-0 w-44 ${isHighlight ? 'scale-105' : ''}`}>
      <div className={`relative rounded-xl p-3 border ${isHighlight ? 'border-green-300 bg-green-50 shadow-md' : 'border-gray-200 bg-white'} h-full`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-1 rounded-md ${config.bg}`}>
            <Icon className={`h-3.5 w-3.5 ${config.color}`} />
          </div>
          <span className="text-[10px] text-gray-400 font-medium">
            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <p className="text-xs font-medium text-gray-800 leading-snug">{event.title}</p>
        {event.company && (
          <span className="inline-block mt-1 text-[10px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">
            {event.company}
          </span>
        )}
      </div>
    </div>
  );
};

const StoryArc = ({ arc }) => {
  const style = OUTCOME_STYLES[arc.outcome] || OUTCOME_STYLES.applied;
  return (
    <div className={`flex-shrink-0 w-56 rounded-xl border-2 ${style.border} ${style.bg} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: arc.color }} />
          <span className="text-xs font-bold text-gray-900">{arc.company}</span>
        </div>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${style.badge}`}>
          {arc.outcome}
        </span>
      </div>
      <p className="text-[10px] text-gray-500 mb-2">{arc.role}</p>
      <div className="space-y-1">
        {(arc.events || []).map((e, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <div className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
            <span className="text-[10px] text-gray-600 leading-snug">{e}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const JourneyTimeline = ({ events = [], storyArcs = [] }) => {
  const scrollRef = useRef(null);
  const arcScrollRef = useRef(null);

  const safeEvents = Array.isArray(events) ? events : [];
  const safeArcs = Array.isArray(storyArcs) ? storyArcs : [];

  const scroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction * 300, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-purple-500">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Your Job Hunt Journey</h3>
      <p className="text-xs text-gray-500 mb-3">Key milestones and events along the way</p>

      <div className="relative">
        {safeEvents.length > 0 ? (
          <>
            <div className="absolute left-0 top-1/2 z-10">
              <button onClick={() => scroll(scrollRef, -1)} className="p-1 bg-white rounded-full shadow border border-gray-200 hover:bg-gray-50">
                <FiChevronLeft className="h-3.5 w-3.5 text-gray-500" />
              </button>
            </div>
            <div className="absolute right-0 top-1/2 z-10">
              <button onClick={() => scroll(scrollRef, 1)} className="p-1 bg-white rounded-full shadow border border-gray-200 hover:bg-gray-50">
                <FiChevronRight className="h-3.5 w-3.5 text-gray-500" />
              </button>
            </div>
            <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-thin scrollbar-thumb-gray-300">
              {safeEvents.map((event, i) => (
                <TimelineEvent key={i} event={event} />
              ))}
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-xs text-gray-400">
            Your milestones (uploads, diagnoses, applications, interviews) will land here as you go.
          </div>
        )}
      </div>

      {safeArcs.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-700">Company Stories</p>
            <div className="flex gap-1">
              <button onClick={() => scroll(arcScrollRef, -1)} className="p-0.5 hover:bg-gray-100 rounded">
                <FiChevronLeft className="h-3.5 w-3.5 text-gray-400" />
              </button>
              <button onClick={() => scroll(arcScrollRef, 1)} className="p-0.5 hover:bg-gray-100 rounded">
                <FiChevronRight className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>
          </div>
          <div ref={arcScrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300">
            {safeArcs.map((arc, i) => (
              <StoryArc key={i} arc={arc} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyTimeline;
